import { ExternalLink, PackageSearch } from 'lucide-react';
import type { RadarSessao, RadarSnapshotItem } from '@/hooks/useRadar';
import {
  SEM_VALOR,
  formatarDataHora,
  formatarMoeda,
  formatarQuantidade,
  paraData,
  textoOuVazio,
} from './radar-format';

interface ItensDoProcessoProps {
  sessao: RadarSessao;
  /** Identificador para a âncora e o `aria-labelledby`. */
  id?: string;
}

const papelLegivel: Record<string, string> = {
  pregoeiro: 'Pregoeiro',
  agente_contratacao: 'Agente de contratação',
  leiloeiro: 'Leiloeiro',
  comissao: 'Comissão de licitação',
};

function Ficha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-lg bg-ds-surface2 p-3">
      <dt className="text-[11px] uppercase tracking-wide text-ds-text-secondary">{rotulo}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ds-text-primary">{valor}</dd>
    </div>
  );
}

function TabelaItens({ itens, titulo }: { itens: RadarSnapshotItem[]; titulo: string }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-ds-border">
      <table className="w-full min-w-[52rem] text-left text-sm">
        <caption className="sr-only">{titulo}</caption>
        <thead className="bg-ds-surface2 text-xs uppercase tracking-wide text-ds-text-secondary">
          <tr>
            <th scope="col" className="p-3">Código</th>
            <th scope="col" className="p-3">Descrição</th>
            <th scope="col" className="p-3">Unidade</th>
            <th scope="col" className="p-3 text-right">Quantidade</th>
            <th scope="col" className="p-3 text-right">Valor de referência</th>
            <th scope="col" className="p-3 text-right">Melhor lance</th>
            <th scope="col" className="p-3">Situação</th>
            <th scope="col" className="p-3">Participação</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item, indice) => (
            <tr key={`${item.codigo ?? indice}-${item.descricao ?? ''}`} className="border-t border-ds-border align-top">
              <th scope="row" className="p-3 text-left font-medium text-ds-text-primary">
                {textoOuVazio(item.codigo ?? indice + 1)}
              </th>
              <td className="p-3 text-ds-text-secondary">{textoOuVazio(item.descricao)}</td>
              <td className="p-3 text-ds-text-secondary">{textoOuVazio(item.unidade)}</td>
              <td className="p-3 text-right text-ds-text-secondary">{formatarQuantidade(item.quantidade)}</td>
              <td className="p-3 text-right tabular-nums">{formatarMoeda(item.valorReferencia)}</td>
              <td className="p-3 text-right font-medium tabular-nums text-ds-text-primary">
                {formatarMoeda(item.melhorLance)}
              </td>
              <td className="p-3 text-ds-text-secondary">{textoOuVazio(item.situacao)}</td>
              <td className="p-3 text-ds-text-secondary">{textoOuVazio(item.participacao)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Espelho dos itens do processo, como o portal oficial os devolveu.
 *
 * O espelho é uma cópia datada: `snapshotAtualizadoEm` fica visível justamente
 * para o usuário saber de quando ela é, e o link para o portal continua à mão.
 */
export function ItensDoProcesso({ sessao, id = 'itens-do-processo' }: ItensDoProcessoProps) {
  const snapshot = sessao.snapshot || {};
  const itens = Array.isArray(snapshot.itens) ? snapshot.itens : [];
  const lotes = Array.isArray(snapshot.lotes) ? snapshot.lotes : [];
  const porLote = Boolean(snapshot.isLote) && lotes.length > 0;
  const linhas = porLote ? lotes : itens;
  const atualizadoEm = sessao.snapshotAtualizadoEm || snapshot.atualizadoEm || null;
  const dataAtualizacao = paraData(atualizadoEm);
  const operador = snapshot.operador || null;
  const papel = operador?.papel ? papelLegivel[operador.papel] || operador.papel : null;
  const aberturaData = paraData(snapshot.dataHoraAbertura);

  const fichas: Array<[string, string]> = [
    ['Situação do processo', textoOuVazio(snapshot.status)],
    ['Tipo de julgamento', textoOuVazio(snapshot.tipoJulgamento)],
    [
      papel ? papel : 'Operador da sessão',
      operador?.nome ? operador.nome : papel ? SEM_VALOR : 'não informado pelo portal',
    ],
  ];
  if (snapshot.tipoLicitacao) fichas.splice(1, 0, ['Tipo de licitação', textoOuVazio(snapshot.tipoLicitacao)]);
  if (snapshot.operacao) fichas.push(['Operação', textoOuVazio(snapshot.operacao)]);

  return (
    <section id={id} aria-labelledby={`${id}-titulo`} className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={`${id}-titulo`} className="font-medium text-ds-text-primary">
          Itens do processo no portal
        </h3>
        <p className="text-xs text-ds-text-secondary">
          {dataAtualizacao ? (
            <>
              Espelho de{' '}
              <time dateTime={dataAtualizacao.toISOString()}>{formatarDataHora(atualizadoEm)}</time>
            </>
          ) : (
            'O portal ainda não informou a data deste espelho.'
          )}
        </p>
      </div>

      <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {fichas.map(([rotulo, valor]) => (
          <Ficha key={rotulo} rotulo={rotulo} valor={valor} />
        ))}
        {aberturaData && (
          <Ficha
            key="abertura"
            rotulo="Abertura da sessão"
            valor={formatarDataHora(snapshot.dataHoraAbertura)}
          />
        )}
      </dl>

      {linhas.length > 0 ? (
        <TabelaItens
          itens={linhas}
          titulo={porLote ? 'Lotes do processo no portal oficial' : 'Itens do processo no portal oficial'}
        />
      ) : (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-ds-border p-4 text-sm text-ds-text-secondary"
        >
          <PackageSearch className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            O portal ainda não devolveu os itens deste processo. Quando devolver, o valor de referência, o melhor lance
            e a situação de cada item aparecem aqui.
          </span>
          {sessao.urlOrigem && (
            <a
              href={sessao.urlOrigem}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-ds-info hover:underline focus:outline-none focus:ring-2 focus:ring-ds-brand"
            >
              Conferir no portal oficial
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      )}

      {porLote && itens.length > 0 && (
        <p className="text-xs text-ds-text-secondary">
          Processo disputado por lote. Os {itens.length} itens que compõem os lotes continuam detalhados no portal
          oficial.
        </p>
      )}
    </section>
  );
}
